import { Injectable } from '@angular/core';

import { BeehiveApiService } from '../beehive-api.service';
import { Payment } from './payment.model';
import { Order, PaymentType } from './lib/payment.interfaces.js';
import { isDiscountCode } from '../discount/lib/discount.util.js';
import {
  ShoppingCart,
  ShoppingCartExtraInfoStore,
  ShoppingCartRegistration,
} from '../../frontend/shopping-cart/shopping-cart.interfaces.js';
import { getRegistrationKey } from '../registration/registration.util';
import { PaymentHistory } from './payment-history.model';
import { ApiQueryParams } from '../api.operators';
import { Observable, tap } from 'rxjs';
import { Customer } from '../customer/customer.model';
import { Staff } from '../staff/staff.model';
import { Organization } from '../organization/organization.model';

@Injectable({ providedIn: 'root' })
export class PaymentService extends BeehiveApiService<Payment> {
  protected entityType = Payment;
  protected endpoint = 'payments';

  checkout<T extends PaymentType>(
    paymentType: PaymentType,
    shoppingCart: ShoppingCart,
    extraInfo?: ShoppingCartExtraInfoStore,
  ) {
    return this.post<any, { payment: Payment<T> }>(['checkout'], {
      type: paymentType,
      registrations: shoppingCart.registrations.map((registration) => ({
        [getRegistrationKey(registration.course ?? registration.workshop)]: (
          registration.course ?? registration.workshop
        ).id,
        attendant: registration.child.id,
        discount: registration.appliedDiscount
          ? isDiscountCode(registration.appliedDiscount)
            ? { code: registration.appliedDiscount.code }
            : { subscription: registration.appliedDiscount.type }
          : undefined,
        extraInformationCamp:
          extraInfo?.[(registration.course ?? registration.workshop).id]
            ?.infoForCamp,
        extraInformationOffice:
          extraInfo?.[(registration.course ?? registration.workshop).id]
            ?.infoForAdmin,
      })),
    });
  }

  createOrder(customer: Customer, registrations: ShoppingCartRegistration[]) {
    return this.post<any, Order>(['checkout'], {
      type: PaymentType.ONFACT,
      registrations: registrations.map((r) => ({
        attendant: r.child.id,
        ...(r.course ? { course: r.course.id } : { workshop: r.workshop.id }),
      })),
      customer: {
        id: customer.id,
      },
    });
  }

  override list(params?: ApiQueryParams<Payment>): Observable<Payment[]> {
    return super
      .list(params)
      .pipe(tap((payments) => payments.forEach(patchHistory)));
  }

  override read(
    id: string,
    rawParams?: ApiQueryParams<Payment>,
  ): Observable<Payment> {
    return super.read(id, rawParams).pipe(tap(patchHistory));
  }
}

// fixme: simply use relation metadata to autoconstruct nested classes
function patchHistory(payment: Payment) {
  if (payment.history) {
    payment.history = new PaymentHistory(payment.history);
    if (payment.history.customer)
      payment.history.customer = new Customer(payment.history.customer);
    if (payment.history.staff)
      payment.history.staff = new Staff(payment.history.staff);
    if (payment.history.organization)
      payment.history.organization = new Organization(
        payment.history.organization,
      );
  }
}
